import React, { useState } from 'react';
import { submitTicket } from '../services/api';

const categories = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'audit_issue', label: 'Audit Issue' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'general', label: 'General Feedback' },
];

const priorities = [
  { value: 'low', label: 'Low', color: 'text-gray-500' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
  { value: 'high', label: 'High', color: 'text-orange-500' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-500' },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      setError('Please fill in both subject and description.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await submitTicket({
        ...form,
        currentPage: window.location.pathname,
      });
      setSubmitted(true);
      setForm({ subject: '', description: '', category: 'general', priority: 'medium' });
      setTimeout(() => { setSubmitted(false); setOpen(false); }, 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => { setOpen(!open); setSubmitted(false); setError(''); }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all hover:scale-110"
        title="Send Feedback"
      >
        {open ? (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        ) : (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
        )}
      </button>

      {/* Feedback panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-96 max-h-[80vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-200">
          {submitted ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3 className="text-lg font-bold text-gray-800">Ticket Submitted!</h3>
              <p className="text-gray-500 text-sm mt-2">We'll review it and get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-5">
              <h3 className="text-lg font-bold text-gray-800 mb-1">Help & Feedback</h3>
              <p className="text-sm text-gray-500 mb-4">Report an issue or share your feedback</p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg mb-3">{error}</div>
              )}

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                  {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <div className="flex gap-2">
                  {priorities.map(p => (
                    <button key={p.value} type="button"
                      onClick={() => setForm({ ...form, priority: p.value })}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                        form.priority === p.value
                          ? 'bg-teal-50 border-teal-500 text-teal-700'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Brief summary of the issue"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4} placeholder="Describe the issue, what you expected, and what happened instead..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none" />
              </div>

              <button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold py-2.5 rounded-xl shadow-md disabled:opacity-50 transition-all">
                {loading ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
